import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Tile, Button, Tag, Select, SelectItem, Tooltip } from '@carbon/react';
import SensorChart from '../components/charts/SensorChart';
import { ArrowLeft, Temperature, BatteryFull, Wifi, Location, Time, ArrowUp, ArrowDown, WarningAlt, Checkmark, Download, Information } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useSubscription } from '../hooks/useSubscription';
import SubscriptionGate from '../components/SubscriptionGate';
import { useSensorsOptimized } from '../hooks/useSensorsOptimized';
import { useAlertRules } from '../hooks/useAlertRules';
import { useRedisReadings } from '../hooks/useRedisReadings';
import { useLiveSource } from '../hooks/useLiveSource';
import { supabase } from '../services/supabaseClient';
import { getBatteryPercentage, formatBatteryDisplay } from '../lib/batteryUtils';
import { getStatusColor, getStatusText, determineStatus } from '../utils/sensorAnalysis';
import './DeviceDetailPage.scss';

const DeviceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { sensors, isLoading: sensorsLoading } = useSensorsOptimized();
  const { rules: alertRules, isLoading: rulesLoading } = useAlertRules();
  const { hasActiveSubscription, loading: subLoading } = useSubscription();
  const isDark = theme === 'dark';
  const [timeRange, setTimeRange] = useState('24h');
  const tempChartRef = useRef(null);
  const humidityChartRef = useRef(null);
  
  // Find the sensor by ID
  const sensor = useMemo(() => {
    return sensors.find(s => s.id === id) || null;
  }, [sensors, id]);
  
  // Track live data source (gateway vs BLE) for real-time icon updates
  const liveSources = useLiveSource(sensor ? [sensor.id] : []);
  const liveSourceData = sensor ? liveSources[sensor.id] : null;
  const liveSource = liveSourceData?.isActive ? liveSourceData.source : null;
  const isLive = liveSourceData?.isActive || false;

  // Use Redis hook for sensor readings (loads instantly, syncs in background)
  const { 
    readings: fullReadings,           // Full resolution for CSV export
    downsampledReadings,              // Downsampled for charts
    isLoading: loadingChart, 
    isSyncing 
  } = useRedisReadings(sensor?.id, timeRange);

  // Convert Redis readings to chart data format (direct approach like SensorPushWeb)
  const { chartData, tempChartData, humidityChartData } = useMemo(() => {
    if (!downsampledReadings || downsampledReadings.length === 0 || !sensor) {
      return { chartData: [], tempChartData: [], humidityChartData: [] };
    }

    const tempData = [];
    const humidityData = [];
    
    downsampledReadings.forEach(reading => {
      // Direct temperature conversion like SensorPushWeb
      let temp = reading.temperature;
      if (sensor.temperature_unit === 'fahrenheit' && temp !== null && temp !== undefined) {
        temp = (temp * 9/5) + 32;
      }
      
      // Direct humidity filtering like SensorPushWeb
      const humidity = (reading.humidity !== null && reading.humidity !== undefined && reading.humidity > 0) 
        ? reading.humidity 
        : null;
      
      // Direct timestamp handling like SensorPushWeb
      const date = new Date(reading.created_at || reading.recorded_at || reading.timestamp);
      
      if (isNaN(date.getTime())) {
        console.warn('Invalid date for reading:', reading);
        return;
      }
      
      // Format for trend calculations (legacy format)
      const formattedDataPoint = {
        time: date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }),
        timestamp: date.getTime(),
        temperature: temp,
        humidity: humidity,
      };
      
      // Format date with full timestamp for data table/modal display
      const dateTimeString = date.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' }) + ' ' +
        date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
      
      // Add temperature data point for Carbon Charts (time series format)
      if (temp !== null && temp !== undefined) {
        tempData.push({
          group: 'Temperature',
          date: date.toISOString(), // ISO format for chart time axis
          dateTime: dateTimeString, // Formatted date-time string for data table/modal display
          value: temp,
        });
      }
      
      // Add humidity data point for Carbon Charts (time series format)
      if (humidity !== null && humidity !== undefined) {
        humidityData.push({
          group: 'Humidity',
          date: date.toISOString(), // ISO format for chart time axis
          dateTime: dateTimeString, // Formatted date-time string for data table/modal display
          value: humidity,
        });
      }
    });

    return {
      chartData: [], // Simplified - not used in current implementation
      tempChartData: tempData,
      humidityChartData: humidityData,
    };
  }, [downsampledReadings, sensor]);

  // Build trend input from downsampled readings (for fallback when sensor has no temperature_status/humidity_status)
  const trendInput = useMemo(() => {
    if (!downsampledReadings || downsampledReadings.length === 0 || !sensor) return [];
    return downsampledReadings
      .map(reading => {
        const ts = new Date(reading.created_at || reading.recorded_at || reading.timestamp).getTime();
        if (isNaN(ts)) return null;
        let temp = reading.temperature;
        if (sensor.temperature_unit === 'fahrenheit' && temp != null) temp = (temp * 9 / 5) + 32;
        const humidity = (reading.humidity != null && reading.humidity > 0) ? reading.humidity : null;
        return { temperature: temp, humidity, timestamp: ts };
      })
      .filter(Boolean)
      .sort((a, b) => a.timestamp - b.timestamp);
  }, [downsampledReadings, sensor]);

  // Get latest reading directly from sensor (like SensorPushWeb)
  const latestReading = sensor?.latest_reading || null;

  // Format device data from sensor
  const device = useMemo(() => {
    if (!sensor) {
      return {
    id: id || '1',
        name: 'Loading...',
    type: 'Temperature & Humidity',
        status: 'offline',
        battery: null,
        signal: 'N/A',
        lastReading: 'No data',
        location: 'N/A',
        currentTemp: null,
        currentHumidity: null,
        tempMin: null,
        tempMax: null,
        humidityMin: null,
        humidityMax: null,
      };
    }

    // Direct approach like SensorPushWeb - use sensor.latest_reading directly
    const currentTemp = latestReading ? latestReading.temperature : null;
    const currentHumidity = latestReading ? latestReading.humidity : null;
    const batteryLevel = latestReading && latestReading.battery_level ? latestReading.battery_level : null;
    const deviceType = sensor.device_type || null;
    // Use actual source from database (mqtt/http/app)
    const source = latestReading?.source || null;

    // Determine status based on last reading time (like SensorPushWeb)
    const getStatus = () => {
      if (!latestReading) return 'offline';
      const readingDate = new Date(latestReading.created_at || latestReading.recorded_at);
      if (isNaN(readingDate.getTime())) return 'offline';
      const now = new Date();
      const diffMs = now - readingDate;
      const diffMins = Math.floor(diffMs / 60000);
      if (diffMins > 60) return 'offline';
      if (diffMins > 15) return 'warning';
      return 'online';
    };

    // Format last reading time (SensorPush style)
    const formatLastReading = () => {
      if (!latestReading) return 'NO DATA';
      // Use created_at for accurate timestamp (not recorded_at which has timezone issues)
      const dateString = latestReading.created_at || latestReading.recorded_at;
      if (!dateString) return 'NO DATA';
      const date = new Date(dateString);
      if (!date || isNaN(date.getTime())) return 'NO DATA';
      
      const day = date.getDate();
      const month = date.toLocaleDateString(undefined, { month: 'short' }).toUpperCase();
      const time = date.toLocaleTimeString(undefined, { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      }).toUpperCase();
      return `${day} ${month} @ ${time}`;
    };

    // Format device type for display
    const formatDeviceType = (type) => {
      if (!type) return 'Unknown';
      const typeMap = {
        'kbeacon': 'KBeacon',
        'sigmawit': 'SIGMAWIT',
        'ela': 'ELA Tag',
      };
      return typeMap[type.toLowerCase()] || type.charAt(0).toUpperCase() + type.slice(1);
    };

    // Get MAC address (device_id or mac_address field)
    const macAddress = sensor.device_id || sensor.mac_address || null;

    return {
      id: sensor.id,
      name: sensor.name,
      type: formatDeviceType(deviceType), // Use device_type, not device_id
      macAddress: macAddress, // Store MAC address separately
      status: getStatus(),
      battery: batteryLevel !== null ? getBatteryPercentage(batteryLevel, deviceType) : null,
      batteryDisplay: batteryLevel !== null ? formatBatteryDisplay(batteryLevel, deviceType, true) : 'N/A',
      batteryLevel: batteryLevel, // Store raw battery level for display
      deviceType: deviceType, // Store device type for display
      signal: 'Good', // Default value - can be enhanced later
      lastReading: formatLastReading(),
      source: source,
      isLive: isLive,
      location: sensor.zone_name || 'Unassigned',
      currentTemp,
      currentHumidity,
      tempMin: null, // Can be fetched from rules/thresholds later
      tempMax: null,
      humidityMin: null,
      humidityMax: null,
    };
  }, [sensor, id, isLive, latestReading]);

  // Extract threshold values from alert rules for this sensor
  const deviceThresholds = useMemo(() => {
    if (!sensor || !alertRules || alertRules.length === 0) {
      console.log('DeviceDetailPage: No sensor or alert rules', { 
        hasSensor: !!sensor, 
        sensorId: sensor?.id, 
        rulesCount: alertRules?.length 
      });
      return { tempMin: null, tempMax: null, humidityMin: null, humidityMax: null };
    }

    console.log('DeviceDetailPage: Checking thresholds', {
      sensorId: sensor.id,
      totalRules: alertRules.length,
      rules: alertRules.map(r => ({
        id: r.id,
        name: r.ruleName,
        isActive: r.isActive,
        sensorIds: r.sensorIds,
        tempMinEnabled: r.tempMinEnabled,
        tempMinValue: r.tempMinValue,
        tempMaxEnabled: r.tempMaxEnabled,
        tempMaxValue: r.tempMaxValue,
        humidityMinEnabled: r.humidityMinEnabled,
        humidityMinValue: r.humidityMinValue,
        humidityMaxEnabled: r.humidityMaxEnabled,
        humidityMaxValue: r.humidityMaxValue,
      }))
    });

    // Find all active rules that apply to this sensor
    // Check if sensor.id (UUID string) matches any sensor_id in the rule's sensorIds array
    const applicableRules = alertRules.filter(rule => {
      const isActive = rule.isActive !== false; // Default to true if undefined
      const hasSensorIds = rule.sensorIds && Array.isArray(rule.sensorIds) && rule.sensorIds.length > 0;
      const matchesSensor = hasSensorIds && rule.sensorIds.some(sensorId => {
        // Compare as strings (case-insensitive) to handle UUID matching
        const sensorIdStr = String(sensorId).toLowerCase().trim();
        const currentSensorIdStr = String(sensor.id).toLowerCase().trim();
        return sensorIdStr === currentSensorIdStr;
      });
      
      console.log('DeviceDetailPage: Rule check', {
        ruleId: rule.id,
        ruleName: rule.ruleName,
        isActive,
        hasSensorIds,
        sensorIds: rule.sensorIds,
        sensorId: sensor.id,
        matchesSensor
      });
      
      return isActive && matchesSensor;
    });

    console.log('DeviceDetailPage: Applicable rules', {
      count: applicableRules.length,
      rules: applicableRules.map(r => ({
        id: r.id,
        name: r.ruleName,
        tempMin: r.tempMinValue,
        tempMax: r.tempMaxValue,
        humidityMin: r.humidityMinValue,
        humidityMax: r.humidityMaxValue,
      }))
    });

    if (applicableRules.length === 0) {
      console.log('DeviceDetailPage: No applicable rules found for sensor', sensor.id);
      return { tempMin: null, tempMax: null, humidityMin: null, humidityMax: null };
    }

    // Aggregate thresholds from all applicable rules
    // For min values, use the maximum (most restrictive - higher minimum)
    // For max values, use the minimum (most restrictive - lower maximum)
    let tempMin = null;
    let tempMax = null;
    let humidityMin = null;
    let humidityMax = null;

    applicableRules.forEach(rule => {
      if (rule.tempMinEnabled && rule.tempMinValue !== null && rule.tempMinValue !== undefined) {
        if (tempMin === null || rule.tempMinValue > tempMin) {
          tempMin = rule.tempMinValue;
        }
      }
      if (rule.tempMaxEnabled && rule.tempMaxValue !== null && rule.tempMaxValue !== undefined) {
        if (tempMax === null || rule.tempMaxValue < tempMax) {
          tempMax = rule.tempMaxValue;
        }
      }
      if (rule.humidityMinEnabled && rule.humidityMinValue !== null && rule.humidityMinValue !== undefined) {
        if (humidityMin === null || rule.humidityMinValue > humidityMin) {
          humidityMin = rule.humidityMinValue;
        }
      }
      if (rule.humidityMaxEnabled && rule.humidityMaxValue !== null && rule.humidityMaxValue !== undefined) {
        if (humidityMax === null || rule.humidityMaxValue < humidityMax) {
          humidityMax = rule.humidityMaxValue;
        }
      }
    });

    const result = { tempMin, tempMax, humidityMin, humidityMax };
    console.log('DeviceDetailPage: Final thresholds result', result);
    return result;
  }, [sensor, alertRules]);
  
  const chartColors = {
    temperature: '#e74c3c',
    humidity: '#2ecc71',
    grid: isDark ? '#393939' : '#e0e0e0',
    text: isDark ? '#c6c6c6' : '#525252',
    bg: isDark ? '#262626' : '#f4f4f4',
  };

  const toDisplayTemp = useCallback(
    (celsius) => {
      if (celsius == null) return null;
      if (sensor?.temperature_unit === 'fahrenheit') return (celsius * 9) / 5 + 32;
      return celsius;
    },
    [sensor?.temperature_unit]
  );

  const tempThresholdDisplay = useMemo(
    () => ({
      min: toDisplayTemp(deviceThresholds.tempMin),
      max: toDisplayTemp(deviceThresholds.tempMax),
    }),
    [deviceThresholds.tempMin, deviceThresholds.tempMax, toDisplayTemp]
  );

  const getChartStats = useCallback((chartData) => {
    const values = (chartData || [])
      .map((d) => d.value)
      .filter((v) => v != null && !Number.isNaN(Number(v)))
      .map(Number);
    if (values.length === 0) return { min: null, max: null, avg: null };
    const min = Math.min(...values);
    const max = Math.max(...values);
    const avg = values.reduce((sum, v) => sum + v, 0) / values.length;
    return { min, max, avg };
  }, []);

  const tempChartStats = useMemo(() => getChartStats(tempChartData), [getChartStats, tempChartData]);
  const humidityChartStats = useMemo(() => getChartStats(humidityChartData), [getChartStats, humidityChartData]);

  const isInRange = (val, min, max) => {
    if (val === null || val === undefined) return false;
    if (min === null || max === null) return true; // No thresholds set
    return val >= min && val <= max;
  };
  const tempOk = isInRange(device.currentTemp, deviceThresholds.tempMin, deviceThresholds.tempMax);
  const humidityOk = isInRange(device.currentHumidity, deviceThresholds.humidityMin, deviceThresholds.humidityMax);

  // Trend time window in hours (same as React Native app default: Status Trend Settings → 30 min)
  const TREND_TIME_WINDOW_HOURS = 0.5;

  // Use trend data from sensor when available; otherwise calculate using same logic as React Native app
  // (determineStatus: filter to time window, sort by time, rate per hour vs thresholds 0.5°C / 2%)
  const tempTrend = useMemo(() => {
    if (sensor?.temperature_status && sensor.temperature_status !== 'UNKNOWN') {
      return {
        trend: sensor.temperature_status,
        change: null,
        hasData: true,
        status: sensor.temperature_status,
        trendText: getStatusText(sensor.temperature_status),
      };
    }
    const tempReadings = trendInput
      .filter(r => r.temperature != null && !Number.isNaN(r.temperature))
      .map(r => ({ value: r.temperature, timestamp: r.timestamp }));
    const status = determineStatus(tempReadings, 'TEMPERATURE', TREND_TIME_WINDOW_HOURS);
    return {
      trend: status,
      change: null,
      hasData: status !== 'UNKNOWN',
      status,
      trendText: getStatusText(status),
    };
  }, [sensor, trendInput]);

  const humidityTrend = useMemo(() => {
    if (sensor?.humidity_status && sensor.humidity_status !== 'UNKNOWN') {
      return {
        trend: sensor.humidity_status,
        change: null,
        hasData: true,
        status: sensor.humidity_status,
        trendText: getStatusText(sensor.humidity_status),
      };
    }
    const humidityReadings = trendInput
      .filter(r => r.humidity != null && !Number.isNaN(r.humidity) && r.humidity > 0)
      .map(r => ({ value: r.humidity, timestamp: r.timestamp }));
    const status = determineStatus(humidityReadings, 'HUMIDITY', TREND_TIME_WINDOW_HOURS);
    return {
      trend: status,
      change: null,
      hasData: status !== 'UNKNOWN',
      status,
      trendText: getStatusText(status),
    };
  }, [sensor, trendInput]);

  // Custom CSV export function - uses full resolution data from Redis cache
  const handleCSVExport = useCallback((dataType, deviceName, chartRef) => {
    // Use full resolution readings for CSV export (not downsampled)
    if (!fullReadings || fullReadings.length === 0) return;
    
    // Format data with full date and time from full resolution readings in LOCAL timezone
    // Match React Native app date parsing: prioritize timestamp, then created_at, then date, then recorded_at
    const csvData = fullReadings.map(reading => {
      // Date parsing: Match React Native app behavior - prioritize timestamp (Date object or number)
      // Then created_at (most reliable), then date, then recorded_at (may have timezone issues)
      let date;
      if (reading.timestamp) {
        date = reading.timestamp instanceof Date ? reading.timestamp : new Date(reading.timestamp);
      } else if (reading.created_at) {
        date = new Date(reading.created_at);
      } else if (reading.date) {
        date = new Date(reading.date);
      } else if (reading.recorded_at) {
        date = new Date(reading.recorded_at);
      } else {
        // Skip invalid readings
        return null;
      }
      
      // Validate date
      if (isNaN(date.getTime())) {
        return null;
      }
      
      // Format as YYYY-MM-DD HH:MM:SS in LOCAL timezone (not UTC)
      const dateTime = date.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' }) + ' ' +
        date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
      
      let value = null;
      if (dataType === 'Temperature') {
        value = reading.temperature;
        // Convert to Fahrenheit if needed
        if (sensor?.temperature_unit === 'fahrenheit' && value !== null && value !== undefined) {
          value = (value * 9/5) + 32;
        }
      } else if (dataType === 'Humidity') {
        value = reading.humidity;
      }
      
      return {
        'Date & Time': dateTime,
        [dataType]: value !== null && value !== undefined ? value.toFixed(2) : ''
      };
    }).filter(row => row !== null); // Filter out null entries from invalid dates

    // Create CSV content
    const headers = Object.keys(csvData[0]);
    const csvRows = [
      headers.join(','),
      ...csvData.map(row => 
        headers.map(header => {
          const value = row[header];
          // Escape commas and quotes in CSV
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      )
    ];
    
    const csvContent = csvRows.join('\n');
    
    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    
    // Use device name in filename
    const sanitizedName = (deviceName || 'Device').replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const timestamp = new Date().toISOString().split('T')[0];
    link.setAttribute('download', `${sanitizedName}_${dataType.toLowerCase()}_${timestamp}.csv`);
    
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [fullReadings, sensor]); // Depend on fullReadings and sensor for temperature unit conversion


  // Format temperature for display
  const formatTemperature = (temp) => {
    if (temp === null || temp === undefined) return '--.-°C';
    const unit = sensor?.temperature_unit === 'fahrenheit' ? '°F' : '°C';
    if (sensor?.temperature_unit === 'fahrenheit') {
      const fahrenheit = (temp * 9/5) + 32;
      return `${fahrenheit.toFixed(1)}${unit}`;
    }
    return `${temp.toFixed(1)}${unit}`;
  };

  if (sensorsLoading) {
    return (
      <div className={`device-detail ${isDark ? 'device-detail--dark' : 'device-detail--light'}`}>
        <div className="device-detail__container">
          <div style={{ padding: '40px', textAlign: 'center', color: isDark ? '#c6c6c6' : '#525252' }}>
            Loading device details...
          </div>
        </div>
      </div>
    );
  }

  if (!sensor) {
    return (
      <div className={`device-detail ${isDark ? 'device-detail--dark' : 'device-detail--light'}`}>
        <div className="device-detail__container">
          <div className="device-detail__header">
            <Button kind="ghost" size="sm" renderIcon={ArrowLeft} onClick={() => navigate('/dashboard/devices')} className="device-detail__back-btn">
              Back
            </Button>
            <div className="device-detail__header-content">
              <h1 className="device-detail__title">Device Not Found</h1>
            </div>
          </div>
          <div style={{ padding: '40px', textAlign: 'center', color: isDark ? '#c6c6c6' : '#525252' }}>
            <p>The device you're looking for doesn't exist or you don't have access to it.</p>
            <Button kind="primary" onClick={() => navigate('/dashboard/devices')} style={{ marginTop: '16px' }}>
              Back to Devices
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <SubscriptionGate
      feature="sensor details, charts, and historical data"
      disabled={!hasActiveSubscription}
      showChildrenGreyed={true}
      customMessage="You need an active subscription to view sensor details, charts, and historical data. Your sensor data is preserved and will be accessible once you resubscribe."
    >
    <div className={`device-detail ${isDark ? 'device-detail--dark' : 'device-detail--light'}`}>
      <div className="device-detail__container">
        <div className="device-detail__header">
          <Button kind="ghost" size="sm" renderIcon={ArrowLeft} onClick={() => navigate('/dashboard/devices')} className="device-detail__back-btn">
            Back
          </Button>
          <div className="device-detail__header-content">
            <h1 className="device-detail__title">{device.name}</h1>
            <div className="device-detail__meta">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Tag type={device.status === 'online' ? 'green' : device.status === 'warning' ? 'warm-gray' : 'red'} size="sm">
                  {device.status.toUpperCase()}
                </Tag>
                {device.status === 'warning' && (
                  <Tooltip
                    align="bottom"
                    label={
                      <div style={{ maxWidth: '250px', padding: '4px' }}>
                        <strong>Warning Status</strong>
                        <br />
                        <br />
                        No readings received in the last 15-60 minutes. This may be normal if your sync settings are configured for periodic updates.
                        <br />
                        <br />
                        <strong>To refresh data:</strong> Use the mobile app to manually sync the device, or check device range, battery level, or connectivity if sync settings allow more frequent updates.
                      </div>
                    }
                  >
                    <button
                      type="button"
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        color: isDark ? '#c6c6c6' : '#525252',
                      }}
                      aria-label="Warning status information"
                    >
                      <Information size={16} />
                    </button>
                  </Tooltip>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span className="device-detail__last-reading">
                  <Time size={14} /> {device.lastReading}
                </span>
                {device.source && (
                  <>
                    {/* Show source as badge: MQTT, HTTP, or APP */}
                    {device.source === 'mqtt' ? (
                      <Tag type="purple" size="sm">MQTT</Tag>
                    ) : device.source === 'http' ? (
                      <Tag type="teal" size="sm">HTTP</Tag>
                    ) : device.source === 'app' ? (
                      <Tag type="blue" size="sm">APP</Tag>
                    ) : (
                      <Tag type="gray" size="sm">{device.source.toUpperCase()}</Tag>
                    )}
                    {/* Show LIVE indicator if actively receiving data */}
                    {device.isLive && (
                      <Tag type="red" size="sm">LIVE</Tag>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="device-detail__time-selector">
          <Select id="timeRange" labelText="" value={timeRange} onChange={(e) => setTimeRange(e.target.value)} size="sm">
            <SelectItem value="1h" text="Last 1 hour" />
            <SelectItem value="6h" text="Last 6 hours" />
            <SelectItem value="24h" text="Last 24 hours" />
            <SelectItem value="7d" text="Last 7 days" />
            <SelectItem value="30d" text="Last 30 days" />
          </Select>
        </div>

        <div className="device-detail__readings">
          <Tile className="device-detail__reading-card">
            <div className="device-detail__reading-icon" style={{ backgroundColor: '#0f62fe20', color: '#0f62fe' }}>
              <Temperature size={24} />
            </div>
            <div className="device-detail__reading-info">
              <span className="device-detail__reading-label">Temperature</span>
              <span className="device-detail__reading-value">{formatTemperature(device.currentTemp)}</span>
              <span className={`device-detail__reading-status device-detail__reading-status--${tempOk ? 'ok' : 'alert'}`}>
                {tempOk ? <Checkmark size={14} /> : <WarningAlt size={14} />}
                {tempOk ? ' Normal' : ' Alert'}
              </span>
              {tempTrend.hasData && (
                <span className="device-detail__reading-trend" style={{ color: getStatusColor(tempTrend.status) }}>
                  {tempTrend.trendText}
                </span>
              )}
            </div>
          </Tile>
          
          <Tile className="device-detail__reading-card">
            <div className="device-detail__reading-icon" style={{ backgroundColor: '#24a14820', color: '#24a148' }}>
              <Temperature size={24} />
            </div>
            <div className="device-detail__reading-info">
              <span className="device-detail__reading-label">Humidity</span>
              <span className="device-detail__reading-value">{device.currentHumidity !== null && device.currentHumidity !== undefined && device.currentHumidity > 0 ? `${device.currentHumidity.toFixed(1)}%` : 'No data'}</span>
              <span className={`device-detail__reading-status device-detail__reading-status--${humidityOk ? 'ok' : 'alert'}`}>
                {humidityOk ? <Checkmark size={14} /> : <WarningAlt size={14} />}
                {humidityOk ? ' Normal' : ' Alert'}
              </span>
              {humidityTrend.hasData && (
                <span className="device-detail__reading-trend" style={{ color: getStatusColor(humidityTrend.status) }}>
                  {humidityTrend.trendText}
                </span>
              )}
            </div>
          </Tile>
        </div>

        <Tile className="device-detail__trends-card">
          <h3 className="device-detail__section-title">Trends</h3>
          <p style={{ fontSize: '0.75rem', color: isDark ? '#8c8c8c' : '#6f6f6f', marginTop: '4px', marginBottom: '12px' }}>
            Based on last 30 min. Rate &gt; 0.5°C/hr or 2%/hr = RISING/FALLING (same logic as mobile app).
          </p>
          <div className="device-detail__trends">
            <div className="device-detail__trend-item">
              <div className="device-detail__trend-header">
                <Temperature size={18} />
                <span>Temperature</span>
              </div>
              {tempTrend.hasData ? (
                <div className={`device-detail__trend-value device-detail__trend-value--${tempTrend.trend === 'RISING' ? 'up' : tempTrend.trend === 'FALLING' ? 'down' : 'stable'}`}
                     style={{ color: getStatusColor(tempTrend.status) }}>
                  {tempTrend.trend === 'RISING' && <ArrowUp size={18} />}
                  {tempTrend.trend === 'FALLING' && <ArrowDown size={18} />}
                  <span className="device-detail__trend-label">
                    {tempTrend.trendText}
                  </span>
                </div>
              ) : (
                <div className="device-detail__trend-value">
                  <span className="device-detail__trend-label" style={{ color: isDark ? '#c6c6c6' : '#525252' }}>No data</span>
                </div>
              )}
            </div>
            <div className="device-detail__trend-item">
              <div className="device-detail__trend-header">
                <Temperature size={18} />
                <span>Humidity</span>
              </div>
              {humidityTrend.hasData ? (
                <div className={`device-detail__trend-value device-detail__trend-value--${humidityTrend.trend === 'RISING' ? 'up' : humidityTrend.trend === 'FALLING' ? 'down' : 'stable'}`}
                     style={{ color: getStatusColor(humidityTrend.status) }}>
                  {humidityTrend.trend === 'RISING' && <ArrowUp size={18} />}
                  {humidityTrend.trend === 'FALLING' && <ArrowDown size={18} />}
                  <span className="device-detail__trend-label">
                    {humidityTrend.trendText}
                  </span>
                </div>
              ) : (
                <div className="device-detail__trend-value">
                  <span className="device-detail__trend-label" style={{ color: isDark ? '#c6c6c6' : '#525252' }}>No data</span>
                </div>
              )}
            </div>
          </div>
        </Tile>

        <Tile className="device-detail__chart-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 className="device-detail__section-title" style={{ margin: 0 }}>Temperature ({sensor?.temperature_unit === 'fahrenheit' ? '°F' : '°C'})</h3>
            {tempChartData.length > 0 && (
              <Button
                kind="ghost"
                size="sm"
                renderIcon={Download}
                onClick={() => handleCSVExport('Temperature', device?.name || sensor?.name, tempChartRef)}
                iconDescription="Export CSV with full date and time"
                hasIconOnly
                tooltipPosition="left"
                tooltipAlignment="end"
              />
            )}
          </div>
          <div className="device-detail__chart" style={{ position: 'relative', paddingBottom: '8px' }}>
            <SensorChart
              data={tempChartData}
              label="Temperature"
              unit={sensor?.temperature_unit === 'fahrenheit' ? '°F' : '°C'}
              color={chartColors.temperature}
              timeRange={timeRange}
              isDark={isDark}
              loading={loadingChart}
              thresholdMin={tempThresholdDisplay.min}
              thresholdMax={tempThresholdDisplay.max}
              statsMin={tempChartStats.min}
              statsMax={tempChartStats.max}
              statsAvg={tempChartStats.avg}
              onExport={(dataType) => handleCSVExport(dataType, device?.name || sensor?.name, tempChartRef)}
            />
          </div>
        </Tile>

        <Tile className="device-detail__chart-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 className="device-detail__section-title" style={{ margin: 0 }}>Humidity (%)</h3>
            {humidityChartData.length > 0 && (
              <Button
                kind="ghost"
                size="sm"
                renderIcon={Download}
                onClick={() => handleCSVExport('Humidity', device?.name || sensor?.name, humidityChartRef)}
                iconDescription="Export CSV"
                hasIconOnly
                tooltipPosition="left"
                tooltipAlignment="end"
              />
            )}
          </div>
          <div className="device-detail__chart" style={{ position: 'relative', paddingBottom: '8px' }}>
            <SensorChart
              data={humidityChartData}
              label="Humidity"
              unit="%"
              color={chartColors.humidity}
              timeRange={timeRange}
              isDark={isDark}
              loading={loadingChart}
              statsMin={humidityChartStats.min}
              statsMax={humidityChartStats.max}
              statsAvg={humidityChartStats.avg}
              onExport={(dataType) => handleCSVExport(dataType, device?.name || sensor?.name, humidityChartRef)}
            />
          </div>
        </Tile>

        <Tile className="device-detail__info-card">
          <h3 className="device-detail__section-title">Device Information</h3>
          <div className="device-detail__info-grid">
            <div className="device-detail__info-item">
              <span className="device-detail__info-label">
                <Location size={16} /> Location
              </span>
              <span className="device-detail__info-value">{device.location}</span>
            </div>
            <div className="device-detail__info-item">
              <span className="device-detail__info-label">
                <BatteryFull size={16} /> Battery
              </span>
              <span className="device-detail__info-value">{device.batteryDisplay}</span>
            </div>
            <div className="device-detail__info-item">
              <span className="device-detail__info-label">
                <Wifi size={16} /> Signal
              </span>
              <span className="device-detail__info-value">{device.signal}</span>
            </div>
            <div className="device-detail__info-item">
              <span className="device-detail__info-label">Type</span>
              <span className="device-detail__info-value">{device.type}</span>
            </div>
          </div>
        </Tile>

        <Tile className="device-detail__info-card">
          <h3 className="device-detail__section-title">Alert Thresholds</h3>
          <p className="device-detail__threshold-description" style={{ 
            fontSize: '0.875rem', 
            color: isDark ? '#c6c6c6' : '#525252',
            marginBottom: '1rem',
            lineHeight: '1.5'
          }}>
            Alert thresholds define the acceptable temperature and humidity ranges for this device. 
            You'll receive notifications when readings fall outside these ranges.
          </p>
          <div className="device-detail__thresholds">
            <div className="device-detail__threshold-row">
              <span>Temperature Range</span>
              <span>
                {deviceThresholds.tempMin !== null || deviceThresholds.tempMax !== null ? (
                  <>
                    {deviceThresholds.tempMin !== null ? formatTemperature(deviceThresholds.tempMin) : 'No min'}
                    {' to '}
                    {deviceThresholds.tempMax !== null ? formatTemperature(deviceThresholds.tempMax) : 'No max'}
                  </>
                ) : (
                  'Not set'
                )}
              </span>
            </div>
            <div className="device-detail__threshold-row">
              <span>Humidity Range</span>
              <span>
                {deviceThresholds.humidityMin !== null || deviceThresholds.humidityMax !== null ? (
                  <>
                    {deviceThresholds.humidityMin !== null ? `${deviceThresholds.humidityMin}%` : 'No min'}
                    {' to '}
                    {deviceThresholds.humidityMax !== null ? `${deviceThresholds.humidityMax}%` : 'No max'}
                  </>
                ) : (
                  'Not set'
                )}
              </span>
            </div>
          </div>
          <Button 
            kind="tertiary" 
            size="sm" 
            className="device-detail__edit-thresholds-btn"
            onClick={() => navigate('/dashboard/rules')}
            style={{ marginTop: '1rem' }}
          >
            Edit Thresholds
          </Button>
        </Tile>
      </div>
    </div>
    </SubscriptionGate>
  );
};

export default DeviceDetailPage;
